import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDeleteContractDraftMutation } from "../../../autogen/bff-api";
import { isNotFoundError } from "../../../common/errors/isNotFoundError";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";

export const DeleteContractDraftButton = ({
  contractId,
  frameworkAgreementId,
}: {
  contractId: string;
  frameworkAgreementId?: string | null;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const confirm = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteDraft] = useDeleteContractDraftMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const leave = () =>
    navigate(
      frameworkAgreementId
        ? urls.app.frameworkAgreements.view.go(frameworkAgreementId)
        : urls.app.contracts.viewAll,
    );

  const onConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteDraft({ contractId }).unwrap();
      leave();
    } catch (e) {
      if (isNotFoundError(e)) {
        leave();
        return;
      }
      trigger(e);
      setIsDeleting(false);
      confirm.onClose();
    }
  };

  return (
    <>
      <Button variant="ghost" colorScheme="red" size="sm" leftIcon={<FiTrash2 />} onClick={confirm.onOpen}>
        {t("Delete draft")}
      </Button>
      <AlertDialog isOpen={confirm.isOpen} leastDestructiveRef={cancelRef} onClose={confirm.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{t("Delete this draft?")}</AlertDialogHeader>
            <AlertDialogBody>
              {t(
                "The draft and everything filled in on it is deleted. If it was started from a need, the need can be started again. This cannot be undone.",
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={confirm.onClose} variant="ghost" isDisabled={isDeleting}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" onClick={onConfirm} ml="3" isLoading={isDeleting}>
                {t("Delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
